import React from 'react';
import { View, Text, StyleSheet, Image } from 'react-native';
import { FeedbackItem, formatFeedbackDate } from '@/types/residents/marketplaceListing';
import { StarRating } from './StarRating';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { useThemeColors } from '@/hooks/residents/useThemeColors';

const PropertyPlaceholder = RESIDENTS_SVG_COMPONENTS.PROPERTY_PLACEHOLDER;

interface FeedbackCardProps {
  feedback: FeedbackItem;
  showDivider?: boolean;
}

export const FeedbackCard: React.FC<FeedbackCardProps> = ({
  feedback,
  showDivider = true,
}) => {
  const { colors } = useThemeColors();

  return (
    <View style={styles.container}>
      {feedback.itemImageUrl ? (
        <Image
          source={{ uri: feedback.itemImageUrl }}
          style={[styles.productImage, { borderColor: colors.card.border }]}
          resizeMode="cover"
        />
      ) : (
        <View style={[styles.productImage, styles.productImagePlaceholder, { borderColor: colors.card.border, backgroundColor: colors.card.bg }]}>
          <PropertyPlaceholder width={24} height={24} />
        </View>
      )}

      <View style={[styles.content, showDivider && { borderBottomWidth: 1, borderBottomColor: colors.separator }]}>
        <View style={styles.topRow}>
          <StarRating rating={feedback.rating} size={16} />
          <Text style={[styles.date, { color: colors.text.dimmer }]}>{formatFeedbackDate(feedback.date)}</Text>
        </View>

        <Text style={[styles.itemTitle, { color: colors.text.primary }]} numberOfLines={2}>
          {feedback.itemTitle}
        </Text>

        {feedback.reviewText && (
          <Text style={[styles.reviewText, { color: colors.text.muted }]}>
            {feedback.reviewText}
          </Text>
        )}

        <Text style={[styles.buyerName, { color: colors.text.dimmer }]}>
          {feedback.reviewerName}
        </Text>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    gap: 16,
    paddingVertical: 10,
  },
  productImage: {
    width: 46,
    height: 46,
    borderRadius: 23,
    borderWidth: 0.45,
  },
  productImagePlaceholder: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  content: {
    flex: 1,
    gap: 4,
    paddingBottom: 16,
  },
  topRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  date: {
    fontSize: 12,
    fontWeight: '300',
    lineHeight: 18,
  },
  itemTitle: {
    fontSize: 14,
    fontWeight: '600',
    lineHeight: 21,
  },
  reviewText: {
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 21,
  },
  buyerName: {
    fontSize: 12,
    fontWeight: '400',
    lineHeight: 18,
  },
});
